import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AgGridReact } from "ag-grid-react";

import { getInvPDFObject } from "./ERPActions/erpActions";
import { COMPONENT_TYPE_NULL } from "../../shared/constants";

import ERPProductSearch from "./ERPProductSearch";
import Filter from "./Filter";
import HeaderFooterComponent from "../printConvertion/HeaderAndFooter";
import { generateBillHtml } from "../printConvertion/DocumentConvert";

function InvoiceViewer({
  ticketId,
  visitId,
  customerId,
  reportType,
  locationIds,
  templateReportType,
  patientId,
  organisationDetail,
  closeInvoiceViewer,
}) {
  const searchInputRef = useRef(null);

  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceResp, setInvoiceResp] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [gridItems, setGridItems] = useState([]);
  const [billSummary, setBillSummary] = useState(null);
  const [printdata, setPrintData] = useState("");
  const [triggerAction, setTriggerAction] = useState("");

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }

    const loadInvoice = async () => {
      try {
        const resp = await getInvPDFObject(
          ticketId,
          visitId,
          customerId,
          reportType,
          locationIds
        );

        setInvoiceResp(resp);

        const invoice = resp?.hmsInvReportDTOList?.[0];
        setInvoiceData(invoice);
        setGridItems(invoice?.billingItem || []);
        setBillSummary({ ...invoice?.billSummary });
      } catch (err) {
        console.error("Invoice load error:", err);
      }
    };

    loadInvoice();
  }, [ticketId, visitId, customerId, reportType, locationIds]);

  const customer = invoiceData?.customerDetail;
  const fmt = (v) => Number(v || 0).toFixed(2);

  const columnDefs = [
    {
      headerName: "Sl No",
      field: "slNo",
      width: 80,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Service Name",
      field: "serviceName",
      flex: 1,
      colSpan: (params) => (params.data?.isConsolidated ? 3 : 1),
      cellRenderer: (params) => params.value,
    },
    {
      headerName: "Charges",
      field: "charges",
      width: 110,
      cellStyle: { textAlign: "right" },
      cellRenderer: (params) =>
        params.data?.isConsolidated ? null : params.value?.toFixed(2),
    },
    {
      headerName: "Units",
      field: "units",
      width: 90,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params) =>
        params.data?.isConsolidated ? null : params.value,
    },
    {
      headerName: "Gross",
      field: "gross",
      width: 110,
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Discount",
      field: "discount",
      width: 110,
      cellStyle: { textAlign: "right" },
    },
    {
      headerName: "Net Amount",
      field: "netAmount",
      width: 130,
      cellStyle: { textAlign: "right" },
    },
  ];

  const handleSelectedLocation = (locationDetail) => {
    setSelectedLocation(locationDetail);
  };

  const updateGridDataFromProdSearch = (productData) => {
    if (!productData) return;

    const amount = Number(productData.priceDiscountIncTax) || 0;

    setGridItems((prev) => {
      const consolidatedIndex = prev.findIndex(
        (item) => item.isConsolidated === true
      );

      const newItem = {
        slNo: 0,
        serviceName: productData.description,
        charges: productData.priceDiscountIncTax,
        units: 1,
        gross: productData.priceDiscountIncTax,
        discount: 0,
        netAmount: productData.priceDiscountIncTax,
        isConsolidated: false,
      };

      const updatedItems =
        consolidatedIndex === -1
          ? [...prev, newItem]
          : [
              ...prev.slice(0, consolidatedIndex),
              newItem,
              ...prev.slice(consolidatedIndex),
            ];

      return updatedItems.map((item, index) => ({
        ...item,
        slNo: index + 1,
      }));
    });

    setBillSummary((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        total: (prev.total || 0) + amount,
        billAmount: (prev.billAmount || 0) + amount,
        paymentAmount: (prev.paymentAmount || 0) + amount,
      };
    });
  };

  const buildUpdatedInvoice = () => {
    if (!invoiceResp) return null;

    return {
      ...invoiceResp,
      hmsInvReportDTOList: [
        {
          ...invoiceResp.hmsInvReportDTOList[0],
          billingItem: gridItems,
          billSummary: billSummary,
        },
      ],
    };
  };

  const handlePreview = async () => {
    const updatedInvoice = buildUpdatedInvoice();

    const data = await generateBillHtml(
      updatedInvoice.hmsInvReportDTOList,
      templateReportType,
      organisationDetail?.orgCode
    );

    setPrintData(data);
    setTriggerAction("preview");
  };

  const handlePrint = async () => {
    const updatedInvoice = buildUpdatedInvoice();

    const data = await generateBillHtml(
      updatedInvoice.hmsInvReportDTOList,
      templateReportType,
      organisationDetail?.orgCode
    );

    setPrintData(data);
    setTriggerAction("print");
  };

  return (
    <Dialog open={true} maxWidth="lg" fullWidth>
      <DialogTitle>
        Invoice 
        <span style={{ fontSize: 14, fontWeight: 500 }}>
         #{customer?.billNumber || ""}
        </span>
        <div
          style={{
            position: "absolute",
            right: 8,
            top: 8,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Filter
            selectedLocation={handleSelectedLocation}
            currentLocation={selectedLocation}
          />
          <IconButton onClick={closeInvoiceViewer}>
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            columnGap: 16,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "110px auto",
              rowGap: 4,
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            <div>Reg No</div>
            <div>: {customer?.regNumber}</div>
            <div>Patient Name</div>
            <div>: {customer?.patientName}</div>
            <div>IP No</div>
            <div>: {customer?.ipNumber}</div>
          </div>

          <div style={{ width: 320 }}>
            <ERPProductSearch
              pageType={COMPONENT_TYPE_NULL}
              patientId={patientId}
              visitId={visitId}
              selectedLocationDetails={selectedLocation}
              productSelected={updateGridDataFromProdSearch}
              searchInputRef={searchInputRef}
            />
          </div>
        </div>

        <div className="ag-theme-balham" style={{ marginBottom: 12 }}>
          <AgGridReact
            rowData={gridItems}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            pagination
            paginationPageSize={10}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 12,
          }}
        >
          <table style={{ minWidth: 220, fontSize: 12, fontWeight: 500 }}>
            <tbody>
              <tr>
                <td>Total :</td>
                <td align="right">{fmt(billSummary?.total)}</td>
              </tr>
              <tr>
                <td>Discount :</td>
                <td align="right">{fmt(billSummary?.discount)}</td>
              </tr>
              <tr>
                <td>Tax :</td>
                <td align="right">{fmt(billSummary?.tax)}</td>
              </tr>
              <tr>
                <td>Round Off :</td>
                <td align="right">{fmt(billSummary?.roundOff)}</td>
              </tr>
              <tr>
                <td>
                  <b>Bill Amount :</b>
                </td>
                <td align="right">
                  <b>{fmt(billSummary?.billAmount)}</b>
                </td>
              </tr>
              <tr>
                <td>Received :</td>
                <td align="right">{fmt(billSummary?.receivedAmount)}</td>
              </tr>
              <tr>
                <td>Balance :</td>
                <td align="right">{fmt(billSummary?.paymentAmount)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="contained" size="small" onClick={handlePreview}>
              Preview
            </Button>
            <Button variant="contained" size="small" onClick={handlePrint}>
              Print
            </Button>
          </div>
        </div>
      </DialogContent>

      {printdata && triggerAction && (
        <HeaderFooterComponent
          title="Invoice"
          data={printdata}
          triggerAction={triggerAction}
          onComplete={() => {
            setPrintData("");
            setTriggerAction("");
          }}
        />
      )}
    </Dialog>
  );
}

export default InvoiceViewer;
